/**
 * Per-site wedding-day timeline shown on the Timeline page. Mirrors the
 * shape produced by wedding-upload-be (functions/src/timeline.ts) and the
 * admin editor. Keep the icon keys in sync across all three.
 */
export type TimelineIconKey =
  | "getting-ready"
  | "first-look"
  | "ceremony"
  | "rings"
  | "vows"
  | "photos"
  | "cocktails"
  | "toast"
  | "dinner"
  | "cake"
  | "speeches"
  | "first-dance"
  | "music"
  | "party"
  | "flowers"
  | "sunset"
  | "sparklers"
  | "transport"
  | "gift"
  | "heart"
  | "venue"
  | "clock";

export interface TimelineEvent {
  id: string;
  /** 24-hour wall-clock time, "HH:mm". */
  time: string;
  name: string;
  description?: string;
  icon: TimelineIconKey;
}

export interface TimelineDay {
  id: string;
  /** Calendar date "YYYY-MM-DD". Days are sorted by this ascending. */
  date: string;
  /** Optional free-form heading shown above the day's events. */
  label?: string;
  events: TimelineEvent[];
}

export interface TimelineConfig {
  /** IANA time zone (e.g. "Europe/Prague") for the live now-highlight. */
  timeZone?: string;
  /** Whether to group events under per-day date headers. Default true. */
  showDates?: boolean;
  days: TimelineDay[];
}

/**
 * Normalizes any stored timeline into the canonical multi-day shape, sorted
 * (days by date, events by time). Tolerates the legacy flat shape
 * ({events,date}) by wrapping it in a single day so older configs keep working.
 */
export function normalizeTimeline(raw: unknown): TimelineConfig {
  if (!raw || typeof raw !== "object") return { days: [] };
  const obj = raw as Record<string, unknown>;
  const timeZone = typeof obj.timeZone === "string" ? obj.timeZone : undefined;
  const showDates =
    typeof obj.showDates === "boolean" ? obj.showDates : undefined;

  let days: TimelineDay[];
  if (Array.isArray(obj.days)) {
    days = obj.days as TimelineDay[];
  } else if (Array.isArray(obj.events)) {
    const date = typeof obj.date === "string" ? obj.date : "";
    days = [{ id: "day-1", date, events: obj.events as TimelineEvent[] }];
  } else {
    days = [];
  }

  const sortedDays = days
    .map((d) => ({
      ...d,
      events: [...(d.events ?? [])].sort((a, b) => a.time.localeCompare(b.time)),
    }))
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  return {
    ...(timeZone ? { timeZone } : {}),
    ...(showDates !== undefined ? { showDates } : {}),
    days: sortedDays,
  };
}

/** Whether a stored timeline (either shape) has at least one event. */
export function hasTimelineEvents(raw: unknown): boolean {
  if (!raw || typeof raw !== "object") return false;
  const obj = raw as Record<string, unknown>;
  if (Array.isArray(obj.days)) {
    return (obj.days as TimelineDay[]).some(
      (d) => Array.isArray(d.events) && d.events.length > 0,
    );
  }
  return Array.isArray(obj.events) && (obj.events as unknown[]).length > 0;
}
